/**
 * Image Edit model + operation registry (frontend single source of truth).
 *
 * Drives the unified "Image Edit" tab (ImageEditTab.tsx), where the user picks
 * a MODEL first and the available operations / fields follow from it:
 *
 *   - Fooocus (local, free on desktop) exposes the full classic operation set
 *     (img2img, face swap, inpaint, outpaint, upscale & vary, enhance,
 *     remove background, describe) — each maps to an existing, working form.
 *   - Cloud AI editors (qwen-edit-2511, nano-banana, ...) expose a single
 *     instruction-based edit (one image + a text instruction).
 *
 * The cloud editor list is NOT a list in this repo any more. It is fetched from
 * GET /api/v1/image-editing/providers by useImageEditModels() below, so the
 * name, description and PRICE the user reads are the ones the server will bill
 * (RULE #-2). Only Fooocus is declared here: it is a local install, not a
 * priced cloud editor, and that endpoint knows nothing about it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Image as ImageIcon,
  Users,
  Brush,
  Maximize2,
  Sparkles,
  Zap,
  Eraser,
  FileImage,
  type LucideIcon,
} from 'lucide-react';
import {
  imageEditingApi,
  imageEditTokensFromUsd,
  type ImageEditProvider,
  type ImageEditProviderInfo,
  type ImageEditQuality,
  type ImageEditSpeed,
} from '@/services/imageEditing';

/** A discrete Fooocus editing operation (each maps to an existing tab form). */
export type FooocusOperationId =
  | 'image-prompt'
  | 'face-swap'
  | 'inpaint'
  | 'outpaint'
  | 'upscale-vary'
  | 'enhance'
  | 'remove-background'
  | 'describe';

export interface FooocusOperation {
  id: FooocusOperationId;
  /** i18n key (under image.imageEdit.operations.*) */
  labelKey: string;
  defaultLabel: string;
  descKey: string;
  defaultDesc: string;
  icon: LucideIcon;
}

/** Ordered Fooocus operations shown when the Fooocus model is selected. */
export const FOOOCUS_OPERATIONS: readonly FooocusOperation[] = [
  {
    id: 'image-prompt',
    labelKey: 'image.imageEdit.operations.imagePrompt',
    defaultLabel: 'Image to Image',
    descKey: 'image.imageEdit.operations.imagePromptDesc',
    defaultDesc: 'Reference images guide style & composition (up to 4)',
    icon: ImageIcon,
  },
  {
    id: 'face-swap',
    labelKey: 'image.imageEdit.operations.faceSwap',
    defaultLabel: 'Face Swap',
    descKey: 'image.imageEdit.operations.faceSwapDesc',
    defaultDesc: 'Put a face onto a new character or scene',
    icon: Users,
  },
  {
    id: 'inpaint',
    labelKey: 'image.imageEdit.operations.inpaint',
    defaultLabel: 'Inpaint',
    descKey: 'image.imageEdit.operations.inpaintDesc',
    defaultDesc: 'Repaint a masked region',
    icon: Brush,
  },
  {
    id: 'outpaint',
    labelKey: 'image.imageEdit.operations.outpaint',
    defaultLabel: 'Outpaint',
    descKey: 'image.imageEdit.operations.outpaintDesc',
    defaultDesc: 'Expand the image beyond its borders',
    icon: Maximize2,
  },
  {
    id: 'upscale-vary',
    labelKey: 'image.imageEdit.operations.upscaleVary',
    defaultLabel: 'Upscale & Vary',
    descKey: 'image.imageEdit.operations.upscaleVaryDesc',
    defaultDesc: 'Upscale or create variations',
    icon: Sparkles,
  },
  {
    id: 'enhance',
    labelKey: 'image.imageEdit.operations.enhance',
    defaultLabel: 'Enhance',
    descKey: 'image.imageEdit.operations.enhanceDesc',
    defaultDesc: 'Improve detail and quality',
    icon: Zap,
  },
  {
    id: 'remove-background',
    labelKey: 'image.imageEdit.operations.removeBackground',
    defaultLabel: 'Remove Background',
    descKey: 'image.imageEdit.operations.removeBackgroundDesc',
    defaultDesc: 'Cut out the subject (transparent PNG)',
    icon: Eraser,
  },
  {
    id: 'describe',
    labelKey: 'image.imageEdit.operations.describe',
    defaultLabel: 'Describe',
    descKey: 'image.imageEdit.operations.describeDesc',
    defaultDesc: 'Generate a prompt from an image',
    icon: FileImage,
  },
];

/**
 * Per-operation behaviour for CLOUD (instruction-based) editors.
 *
 * Cloud editors take one image + one text instruction, so an "operation" is
 * just an editable starter instruction (+ a help line). Some Fooocus
 * operations don't map to a pure instruction (outpaint/upscale need real canvas
 * work; describe is a reverse-prompt, not an edit) → marked unsupported and
 * shown disabled with a "best with Fooocus" hint.
 */
export interface CloudOperationPreset {
  supported: boolean;
  /** i18n key + English fallback for the starter instruction inserted on click. */
  templateKey?: string;
  defaultTemplate?: string;
  /** i18n key + English fallback for the help line under the instruction box. */
  hintKey?: string;
  defaultHint?: string;
}

export const CLOUD_OPERATION_PRESETS: Record<FooocusOperationId, CloudOperationPreset> = {
  'image-prompt': {
    supported: true,
    templateKey: 'image.imageEdit.cloudPresets.imagePrompt',
    defaultTemplate: '',
    hintKey: 'image.imageEdit.cloudHints.imagePrompt',
    defaultHint: 'Describe any change to the image in plain language.',
  },
  'face-swap': {
    supported: true,
    templateKey: 'image.imageEdit.cloudPresets.faceSwap',
    defaultTemplate:
      'Swap the face with [describe the person, e.g. Lionel Messi], keeping the original pose, angle and lighting.',
    hintKey: 'image.imageEdit.cloudHints.faceSwap',
    defaultHint:
      'Works best with a well-known person the model recognizes. To swap in a face from your own photo, use the Fooocus model.',
  },
  inpaint: {
    supported: true,
    templateKey: 'image.imageEdit.cloudPresets.inpaint',
    defaultTemplate:
      'Replace [the area to change] with [the new content], blending naturally with the rest of the image.',
    hintKey: 'image.imageEdit.cloudHints.inpaint',
    defaultHint: 'Describe the region and the replacement in words — no mask needed.',
  },
  outpaint: { supported: false },
  'upscale-vary': { supported: false },
  enhance: {
    supported: true,
    templateKey: 'image.imageEdit.cloudPresets.enhance',
    defaultTemplate:
      'Enhance the image quality: sharpen details, improve lighting and colors, and reduce noise. Keep the content unchanged.',
    hintKey: 'image.imageEdit.cloudHints.enhance',
    defaultHint: '',
  },
  'remove-background': {
    supported: true,
    templateKey: 'image.imageEdit.cloudPresets.removeBackground',
    defaultTemplate:
      'Remove the background completely and place the main subject on a plain white background.',
    hintKey: 'image.imageEdit.cloudHints.removeBackground',
    defaultHint: 'For a true transparent PNG (alpha channel), use the Fooocus model.',
  },
  describe: { supported: false },
};

/** Model kinds available in the unified Image Edit tab. */
export type ImageEditModelKind = 'fooocus' | 'ai-editor';

/** The local Fooocus model id (default selection). */
export const FOOOCUS_MODEL_ID = 'fooocus';

export interface ImageEditModelOption {
  /** 'fooocus' or an ImageEditProvider slug */
  id: string;
  kind: ImageEditModelKind;
  name: string;
  description: string;
  /**
   * True when this model TYPE has a local execution path on the user's
   * machine (routing capability). This is NOT "is free right now" — Fooocus
   * is routing_mode=hybrid (fooocus_runpod on RunPod when no local install is
   * present), so a capability of `true` does not mean the CURRENT run is
   * free. Callers that need "is it actually free right now" must combine
   * this with the real install state (useImageProviderAvailability() ->
   * is_local && is_installed), never use this flag alone for billing/price
   * display — that conflation (capability == free) was the bug behind the
   * ImageEditTab "Free" badge lying to users without a local Fooocus install.
   */
  isLocalCapable: boolean;
  /** Cloud editors only: what one edit costs the account, in tokens. */
  tokensPerEdit?: number;
  /**
   * Cloud editors only: true when a larger output costs MORE than
   * `tokensPerEdit`, which is then the price at the backend's default size.
   * Published as `pricedBySize`; nano-banana-pro-edit's 4k tier is ~1.7x its 1k.
   */
  pricedBySize?: boolean;
  /** Cloud editors only: account-paid tokens per resolution tier, when tiered. */
  tierTokensPerEdit?: Record<string, number>;
  quality?: ImageEditQuality;
  speed?: ImageEditSpeed;
}

/**
 * The one locally-installable model. Everything else is fetched.
 *
 * It carries no `tokensPerEdit` on purpose: when Fooocus is not installed
 * locally it is routed to RunPod and billed, and that price belongs to the
 * image-availability/cost endpoints, not to a constant in this file.
 */
export const FOOOCUS_MODEL: ImageEditModelOption = {
  id: FOOOCUS_MODEL_ID,
  kind: 'fooocus',
  name: 'Fooocus',
  description:
    'Local SDXL — img2img, face swap, inpaint, outpaint, upscale, enhance, remove BG, describe',
  isLocalCapable: true,
};

/** Default model id for the unified Image Edit tab. */
export const DEFAULT_IMAGE_EDIT_MODEL_ID = FOOOCUS_MODEL_ID;

/** Project one fetched provider row onto the tab's model shape. */
export function toImageEditModel(provider: ImageEditProviderInfo): ImageEditModelOption {
  return {
    id: provider.id,
    kind: 'ai-editor',
    name: provider.name,
    description: provider.description,
    isLocalCapable: false,
    tokensPerEdit: provider.tokensPerEdit,
    pricedBySize: provider.pricedBySize,
    tierTokensPerEdit: provider.tierCostPerEdit
      ? Object.fromEntries(
          Object.entries(provider.tierCostPerEdit).map(([tier, usd]) => [
            tier,
            // Same conversion as the headline price -- one function, so a tier
            // and the default size can never be quoted on different scales.
            imageEditTokensFromUsd(usd),
          ])
        )
      : undefined,
    quality: provider.quality,
    speed: provider.speed,
  };
}

/** Look up a model option by id (falls back to Fooocus, which always exists). */
export function getImageEditModel(
  models: readonly ImageEditModelOption[],
  id: string
): ImageEditModelOption {
  return models.find((m) => m.id === id) ?? FOOOCUS_MODEL;
}

// In-process cache so ImageEditTab and the AIEditTab it renders share ONE
// request, the way useImageProviderAvailability does. There is no TTL: a price
// list that changed mid-session is a deploy, and refresh() exists for that.
let _moduleCache: {
  models: ImageEditModelOption[];
  defaultProviderId: ImageEditProvider;
  enabled: boolean;
} | null = null;

export interface UseImageEditModelsResult {
  /** Fooocus plus every cloud editor the server published. */
  models: ImageEditModelOption[];
  localModels: ImageEditModelOption[];
  /** Empty while loading AND when the fetch failed — never a guessed list. */
  cloudModels: ImageEditModelOption[];
  /** The server's default editor; null until the catalog loads. */
  defaultProviderId: ImageEditProvider | null;
  /** Whether this server can actually run a cloud edit right now. */
  enabled: boolean;
  isLoading: boolean;
  /** Non-null when the catalog could not be fetched: prices are UNKNOWN. */
  error: string | null;
  refresh: () => void;
}

/**
 * Fetch the editors and their prices.
 *
 * There is deliberately no offline default: when this fails the caller shows
 * "price unavailable", not a number (RULE #5). The whole point of deleting the
 * hardcoded array was that a stale price is worse than a missing one -- it was
 * showing 30 tokens for an edit billed at 57.
 */
export function useImageEditModels(): UseImageEditModelsResult {
  const [models, setModels] = useState<ImageEditModelOption[]>(
    _moduleCache?.models ?? [FOOOCUS_MODEL]
  );
  const [defaultProviderId, setDefaultProviderId] = useState<ImageEditProvider | null>(
    _moduleCache?.defaultProviderId ?? null
  );
  const [enabled, setEnabled] = useState<boolean>(_moduleCache?.enabled ?? false);
  const [isLoading, setIsLoading] = useState<boolean>(_moduleCache === null);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const cancelledRef = useRef(false);

  const refresh = useCallback((): void => {
    _moduleCache = null;
    setRefreshToken((n) => n + 1);
  }, []);

  useEffect(() => {
    cancelledRef.current = false;

    if (_moduleCache !== null) {
      setModels(_moduleCache.models);
      setDefaultProviderId(_moduleCache.defaultProviderId);
      setEnabled(_moduleCache.enabled);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);

    const load = async (): Promise<void> => {
      try {
        const catalog = await imageEditingApi.getProviders();
        if (cancelledRef.current) return;

        const next = {
          models: [FOOOCUS_MODEL, ...catalog.providers.map(toImageEditModel)],
          defaultProviderId: catalog.default,
          enabled: catalog.enabled,
        };
        _moduleCache = next;

        setModels(next.models);
        setDefaultProviderId(next.defaultProviderId);
        setEnabled(next.enabled);
        setError(null);
        setIsLoading(false);
      } catch (err: unknown) {
        if (cancelledRef.current) return;
        // No cloud editor survives a failed catalog fetch: we would not know
        // its name, let alone its price. Fooocus is unaffected -- it is local.
        setModels([FOOOCUS_MODEL]);
        setDefaultProviderId(null);
        setEnabled(false);
        setError(err instanceof Error ? err.message : String(err));
        setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelledRef.current = true;
    };
  }, [refreshToken]);

  return {
    models,
    localModels: models.filter((m) => m.isLocalCapable),
    cloudModels: models.filter((m) => !m.isLocalCapable),
    defaultProviderId,
    enabled,
    isLoading,
    error,
    refresh,
  };
}
